export type LeaveType = "annual" | "circumstantial" | "rehabilitation" | "care" | "training" | "other";
export type LeaveStatus = "pending" | "approved" | "rejected" | "cancellation_pending" | "cancelled" | "withdrawn";

export type SickLeaveStatus = "pending" | "verified" | "rejected";

export interface SickLeave {
  id: string;
  employee: string;
  employee_name: string;
  start_date: string;
  end_date: string;
  days_count: number;
  note: string;
  submitted_by: string | null;
  submitted_by_name: string | null;
  status: SickLeaveStatus;
  status_display: string;
  verified_by: string | null;
  verified_by_name: string | null;
  verified_at: string | null;
  verification_comment: string;
  created_at: string;
  updated_at: string;
}

export const SICK_LEAVE_STATUS_COLORS: Record<SickLeaveStatus, "yellow" | "green" | "red"> = {
  pending: "yellow",
  verified: "green",
  rejected: "red",
};

export interface LeaveRequest {
  id: string;
  employee: string;
  employee_name: string;
  leave_type: LeaveType;
  leave_type_display: string;
  other_type: string;
  is_hourly: boolean;
  start_date: string;
  end_date: string;
  days_count: number;
  hours_count: number | null;
  minutes_count: number | null;
  note: string;
  attachment_managed_file: string | null;
  attachment_managed_file_detail: {
    id: string;
    original_filename: string;
    content_type: string;
    size_bytes: number;
    /** Download link (attachment disposition — saves the file). */
    url: string | null;
    /** Preview link (inline disposition — renders in an iframe/img). */
    inline_url: string | null;
  } | null;
  submitted_by: string | null;
  submitted_by_name: string | null;
  status: LeaveStatus;
  status_display: string;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_comment: string;
  cancellation_comment: string;
  available_annual_days: number | null;
  available_annual_hours: number | null;
  available_annual_minutes: number | null;
  employee_fraction_numerator: number;
  employee_fraction_denominator: number;
  created_at: string;
  updated_at: string;
}

/** An employee a reviewer may file a leave request on behalf of. */
export interface LeaveProxyTarget {
  id: string;
  full_name: string;
  role: string;
  contract_type: string | null;
  /** False for recently-deactivated employees still within the proxy grace period. */
  is_active: boolean;
}

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  annual: "Urlop wypoczynkowy",
  circumstantial: "Urlop okolicznościowy",
  rehabilitation: "Urlop rehabilitacyjny",
  care: "Urlop opiekuńczy",
  training: "Urlop szkoleniowy",
  other: "Urlop inny",
};

/** Returns "Xh Ymin" equivalent for a part-time non-hourly leave, or null for full-time. */
export function fmtPartTimeDuration(days: number, num: number, den: number): string | null {
  if (num >= den) return null;
  const totalMin = Math.round(days * 480 * num / den);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
}

export function fmtHourlyDuration(hours: number | null, minutes: number | null): string {
  const h = hours ?? 0;
  const m = minutes ?? 0;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
}

export const LEAVE_STATUS_COLORS: Record<LeaveStatus, "yellow" | "green" | "red" | "gray"> = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
  cancellation_pending: "yellow",
  cancelled: "red",
  withdrawn: "gray",
};

export interface AnnualLeaveEntitlement {
  id: string;
  employee: string;
  year: number;
  days_total: number;
  prev_year_carryover: number;
  /** Zbiorczy bilans otwarcia — urlop wykorzystany poza ELGW5, w minutach (1 dzień = 480). */
  historical_used_minutes: number;
  /** Suma pojedynczych historycznych absencji wypoczynkowych w tym roku, w minutach. */
  historical_entries_minutes: number;
  historical_note: string;
  /**
   * Ręczne nadpisania wykorzystania per miesiąc (12 pozycji, minuty; null =
   * miesiąc liczony z wniosków ELGW5 i wpisów). Wartość ZASTĘPUJE wyliczenie
   * systemowe dla danego miesiąca.
   */
  manual_monthly_minutes: (number | null)[] | null;
  used_days: number;
  used_hours_remainder: number;
  used_minutes_remainder: number;
  available_days: number;
  available_hours_remainder: number;
  available_minutes_remainder: number;
  available_overdue_days: number;
  available_overdue_hours_remainder: number;
  available_overdue_minutes_remainder: number;
  available_current_days: number;
  available_current_hours_remainder: number;
  available_current_minutes_remainder: number;
}

export type HistoricalLeaveSource = "karta_pracy" | "wniosek_papierowy" | "inne";

export const HISTORICAL_LEAVE_SOURCE_LABELS: Record<HistoricalLeaveSource, string> = {
  karta_pracy: "Karta pracy",
  wniosek_papierowy: "Wniosek papierowy",
  inne: "Inne",
};

/** Historyczna absencja sprzed ELGW5 — liczby dni/godzin wpisywane ręcznie przez kadry. */
export interface HistoricalLeave {
  id: string;
  employee: string;
  employee_name: string;
  leave_type: LeaveType;
  leave_type_display: string;
  start_date: string;
  end_date: string;
  days_count: number;
  hours_count: number | null;
  minutes_count: number | null;
  source: HistoricalLeaveSource;
  source_display: string;
  note: string;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

interface LeaveBalanceTotals extends AnnualLeaveEntitlement {
  system_used_minutes: number;
  historical_used_total_minutes: number;
  used_minutes_total: number;
  available_minutes_total: number;
  /** False = rok bez żadnych danych (sprzed ELGW5) — stub do uzupełnienia przez kadry. */
  has_data: boolean;
}

export interface LeaveBalanceReportRow extends LeaveBalanceTotals {
  employee_name: string;
  branch_name: string;
  contract_type: string | null;
}

export interface LeaveCardYear extends LeaveBalanceTotals {
  /** 12 pozycji (styczeń–grudzień): efektywne minuty urlopu w miesiącu (nadpisanie albo wyliczenie). */
  months_minutes: number[];
  /** 12 pozycji: wartość wyliczona przez system (wnioski ELGW5 + wpisy ręczne), bez nadpisań. */
  months_system_minutes: number[];
}

export interface LeaveCard {
  employee: {
    id: string;
    name: string;
    branch_name: string;
    contract_type: string | null;
    employment_fraction_numerator: number;
    employment_fraction_denominator: number;
    /** Efektywna data zatrudnienia: nadpisanie kadr albo data z kosztów płac. */
    employment_start_date: string | null;
    /** Ręczne nadpisanie daty zatrudnienia (pracownicy sprzed ELGW5). */
    employment_start_override: string | null;
  };
  years: LeaveCardYear[];
  system_leaves: LeaveRequest[];
  historical_leaves: HistoricalLeave[];
}

/** "8 dni 2h 30min" — pomija zerowe człony; "0 dni" dla zera. Ujemne z minusem. */
export function fmtMinutesAsDays(minutes: number): string {
  const sign = minutes < 0 ? "-" : "";
  const abs = Math.abs(minutes);
  const days = Math.floor(abs / 480);
  const hours = Math.floor((abs % 480) / 60);
  const mins = abs % 60;
  const parts: string[] = [];
  if (days) parts.push(days === 1 ? "1 dzień" : `${days} dni`);
  if (hours) parts.push(`${hours}h`);
  if (mins) parts.push(`${mins}min`);
  return sign + (parts.length ? parts.join(" ") : "0 dni");
}
